import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ClipboardList, FileText, Clock, CheckCircle, RotateCcw, Search, User } from 'lucide-react';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TASK_CONFIG = {
  assigned_to_me: { icon: FileText, color: 'bg-emerald-50 text-emerald-700', border: 'border-l-emerald-500' },
  draft_pending: { icon: FileText, color: 'bg-gray-100 text-gray-600', border: 'border-l-gray-400' },
  review_pending: { icon: Clock, color: 'bg-amber-50 text-amber-700', border: 'border-l-amber-400' },
  finalize_pending: { icon: CheckCircle, color: 'bg-blue-50 text-blue-700', border: 'border-l-blue-400' },
  returned: { icon: RotateCcw, color: 'bg-red-50 text-red-600', border: 'border-l-red-400' },
};

const SECTION_LABELS = {
  assigned_to_me: 'Atribuídos para Você',
  review_pending: 'Aguardando Revisão',
  finalize_pending: 'Aguardando Finalização',
  draft_pending: 'Rascunhos Pendentes',
  returned: 'Devolvidos para Correção',
};

const MyTasksPage = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [activeFilter, setActiveFilter] = useState('mine');

  const loadUser = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/me`, { withCredentials: true });
      setCurrentUser(res.data);
    } catch (error) {
      console.error('Failed to load user:', error);
    }
  }, []);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/my-tasks`, {
        withCredentials: true,
        params: { filter: activeFilter }
      });
      setTasks(res.data);
    } catch (error) {
      console.error('Failed to load tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const hasGlobalView = currentUser?.role === 'Administrador' || currentUser?.role === 'Leitor/Gestor';

  const filtered = tasks.filter(t =>
    !search ||
    t.title?.toLowerCase().includes(search.toLowerCase()) ||
    t.client_name?.toLowerCase().includes(search.toLowerCase()) ||
    t.task_label?.toLowerCase().includes(search.toLowerCase()) ||
    t.assigned_to_name?.toLowerCase().includes(search.toLowerCase())
  );

  const grouped = {
    assigned_to_me: filtered.filter(t => t.task_type === 'assigned_to_me'),
    review_pending: filtered.filter(t => t.task_type === 'review_pending'),
    finalize_pending: filtered.filter(t => t.task_type === 'finalize_pending'),
    draft_pending: filtered.filter(t => t.task_type === 'draft_pending'),
    returned: filtered.filter(t => t.task_type === 'returned'),
  };

  const FILTER_OPTIONS = [
    { key: 'mine', label: 'Minhas' },
    { key: 'others', label: 'Terceiros' },
    { key: 'all', label: 'Todas' },
  ];

  if (loading) {
    return <div className="flex justify-center p-12"><div className="w-12 h-12 border-4 border-[#AF2628] border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="my-tasks-page">
      <div>
        <h1 className="text-3xl font-bold text-[#111111]">Minhas Tarefas</h1>
        <p className="text-[#6E6E73]">{tasks.length} tarefa{tasks.length !== 1 ? 's' : ''} pendente{tasks.length !== 1 ? 's' : ''}</p>
      </div>

      <div className="bg-white border border-[#E5E5EA] rounded-xl p-4">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Search className="w-5 h-5 text-[#6E6E73]" />
            <Input
              placeholder="Filtrar tarefas..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1"
              data-testid="tasks-search-input"
            />
          </div>
          {hasGlobalView && (
            <div className="flex items-center gap-1 bg-[#F7F7F8] rounded-lg p-1" data-testid="task-filter-group">
              {FILTER_OPTIONS.map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setActiveFilter(opt.key)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    activeFilter === opt.key
                      ? 'bg-[#1B3A5C] text-white'
                      : 'text-[#6E6E73] hover:bg-white'
                  }`}
                  data-testid={`filter-${opt.key}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-12">
            <ClipboardList className="w-12 h-12 text-[#AEAEB2] mx-auto mb-3" />
            <p className="text-[#6E6E73]">Nenhuma tarefa pendente</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(grouped).map(([type, items]) => {
              if (items.length === 0) return null;
              const cfg = TASK_CONFIG[type];
              return (
                <div key={type}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-2 h-2 rounded-full ${cfg.border.replace('border-l-', 'bg-')}`} />
                    <h3 className="text-sm font-semibold text-[#6E6E73] uppercase tracking-wide">{SECTION_LABELS[type]}</h3>
                    <span className="text-xs bg-[#F7F7F8] px-2 py-0.5 rounded-full text-[#6E6E73]">{items.length}</span>
                  </div>
                  <div className="space-y-2">
                    {items.map((task) => {
                      const Icon = cfg.icon;
                      return (
                        <div
                          key={`${task.document_id}-${type}`}
                          onClick={() => navigate(`/documents/${task.document_id}`)}
                          className={`border border-[#E5E5EA] border-l-4 ${cfg.border} rounded-lg p-4 hover:shadow-md cursor-pointer transition-all`}
                          data-testid={`task-${task.document_id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center ${cfg.color}`}>
                                <Icon className="w-4 h-4" />
                              </div>
                              <div>
                                <h4 className="font-medium text-[#111111]">{task.title}</h4>
                                <div className="flex items-center gap-2 text-xs text-[#6E6E73] flex-wrap">
                                  <span>{task.tipo}</span>
                                  <span>·</span>
                                  <span>{task.client_name}</span>
                                  <span>·</span>
                                  <span>{new Date(task.updated_at).toLocaleDateString('pt-BR')}</span>
                                  {task.assigned_to_name && (
                                    <>
                                      <span>·</span>
                                      <span className="flex items-center gap-1 text-[#2B5C8A] font-medium">
                                        <User className="w-3 h-3" />
                                        {task.assigned_to_name}
                                      </span>
                                    </>
                                  )}
                                  {activeFilter !== 'mine' && task.created_by_name && (
                                    <>
                                      <span>·</span>
                                      <span className="text-[#6E6E73]">Criado por: {task.created_by_name}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${cfg.color}`}>
                              {task.task_label}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyTasksPage;
